import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import Notification from './Notification';
import FullscreenNotification, {
  FULLSCREEN_BUTTON_WIDTH,
} from './FullscreenNotification';
import { getFontFaceFromStyle } from 'support/textUtils';
import { constants } from 'aliases';
import Button, { ButtonTemplateSpec } from 'components/common/buttons/Button';
import {
  HoverableComponent,
  LocalCoordsFromClick,
} from 'components/common/HoverableComponent';
import { BrazeButton } from 'types/braze';
import {
  MessageCallbackProps,
  DisplayType,
} from 'services/NotificationService';
import { convertHexColor } from 'support/generalUtils';

export interface BrazeNotificationTemplateSpec
  extends Lightning.Component.TemplateSpec {
  imageUrl: string;
  color: number;

  HoverBlocker: typeof Lightning.Texture;
  Content: {
    Notification: typeof Notification;
    FullscreenNotification: typeof FullscreenNotification;
  };
}

const BUTTON_HEIGHT = 62;
const BUTTON_WIDTH = 400;
const BUTTON_FONT_SIZE = 23;
const BUTTON_MARGIN = 16;
const NOTIFICATION_WIDTH = 432;
const POSITION_X = 1388;
const POSITION_Y = 485;
const HEIGHT = 515;
const PAGE_WIDTH = 1920;
const PAGE_HEIGHT = 1080;

const CONTENT_LAYOUT = {
  w: NOTIFICATION_WIDTH,
  h: HEIGHT,
  x: POSITION_X,
  y: POSITION_Y,
};

const FULLSCREEN_CONTENT_LAYOUT = {
  w: PAGE_WIDTH,
  h: PAGE_HEIGHT,
  x: 0,
  y: 0,
};

export default class BrazeNotification extends HoverableComponent<BrazeNotificationTemplateSpec> {
  private _HoverBlocker = this.getByRef('HoverBlocker')!;
  private _Content = this.getByRef('Content')!;
  private _Notification = this._Content.getByRef('Notification')!;
  private _FullscreenNotification = this._Content.getByRef(
    'FullscreenNotification',
  )!;
  private _onViewed: (() => void) | null = null;
  private _onButtonClick: ((index: number) => void) | null = null;

  private _displayType: DisplayType = 'notification';

  static override _template(): Lightning.Component.Template<BrazeNotificationTemplateSpec> {
    return {
      HoverBlocker: {
        w: PAGE_WIDTH,
        h: PAGE_HEIGHT,
        rect: true,
        visible: false,
        collision: true,
        zIndex: constants.ui.zIndex.notificationLayer - 1,
      },
      Content: {
        ...CONTENT_LAYOUT,
        visible: false,
        zIndex: constants.ui.zIndex.notificationLayer,
        flex: {
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
        },
        Notification: {
          type: Notification,
          visible: false,
        },
        FullscreenNotification: {
          type: FullscreenNotification,
          visible: false,
        },
      },
    };
  }

  override _getFocused() {
    return this.getActiveNotification();
  }

  override _handleBack() {
    this.$dismiss();
    return true;
  }

  override _handleKey() {
    return true;
  }

  override _handleClick(
    target: Lightning.Component,
    localCoords: LocalCoordsFromClick,
  ) {
    if (target === this._HoverBlocker) return true;
    return super._handleClick(target, localCoords);
  }

  modalInit(data: MessageCallbackProps) {
    this._onViewed = data.onViewed;
    this._onButtonClick = data.onButtonClick;
    this._displayType = data.displayType;

    this._Content.patch(
      this.isFullscreen() ? FULLSCREEN_CONTENT_LAYOUT : CONTENT_LAYOUT,
    );

    const message = data.message;
    const payload = {
      imageUrl: message.imageUrl ?? '',
      backgroundColor:
        message.backgroundColor ?? Colors('modalBackground').get(),
      headerTextColor: message.headerTextColor,
      messageTextColor: message.textColor,
      titleLabel: message.header ?? '',
      descLabel: message.message ?? '',
      ...(message.extras.hasBorder && message.extras.borderColor
        ? { borderColor: convertHexColor(message.extras.borderColor) }
        : {}),
    };

    this._HoverBlocker.patch({
      color: message.frameColor,
    });

    this.getActiveNotification().patch(payload);
    this.updateButtons(message.buttons ?? []);
  }

  show() {
    this.updateVisibility(true);
    this._onViewed?.();
  }

  hide() {
    this.updateVisibility(false);
    this.clearCallbacks();
  }

  private getBaseButton() {
    return {
      type: Button,
      height: BUTTON_HEIGHT,
      minWidth: this.isFullscreen() ? FULLSCREEN_BUTTON_WIDTH : BUTTON_WIDTH,
      fontSize: BUTTON_FONT_SIZE,
      fontFace: getFontFaceFromStyle('medium'),
      margin: BUTTON_MARGIN,
      zIndex: constants.ui.zIndex.notificationLayer + 1,
      backgroundColor: {
        unfocused: Colors('grey').get(),
        focused: Colors('appAccent').get(),
      },
      fontColor: {
        unfocused: Colors('notificationButtonLabel').get(),
        focused: Colors('labelText').get(),
      },
    };
  }

  private updateButtons(buttons: BrazeButton[]) {
    const buttonStack = buttons.map(button => this.generateButton(button));

    this.getActiveNotification().updateButtons(buttonStack);
    this._refocus();
  }

  private isNavigateButton(button: BrazeButton): boolean {
    return button.clickAction?.toLowerCase() === 'uri';
  }

  private generateButton(
    button: BrazeButton,
  ): Lightning.Element.PatchTemplate<ButtonTemplateSpec> {
    const isNavigateButton = this.isNavigateButton(button);
    const action = isNavigateButton ? '$navigateAction' : '$dismiss';

    return {
      ...this.getBaseButton(),
      label: button.text,
      action,
      actionArgs: isNavigateButton
        ? { id: button.id, clickAction: button.clickAction, uri: button.uri }
        : { id: button.id },
      signals: { $onHover: '$onHover' },
      passSignals: { [action]: true },
    };
  }

  private $navigateAction(actionArgs: {
    id: number;
    clickAction: string;
    uri: string;
  }) {
    this._onButtonClick?.(actionArgs.id);

    if (actionArgs.clickAction.toLowerCase() === 'uri') {
      AppData?.device.handleBrandDeepLink(actionArgs.uri);
    }
  }

  private $dismiss(args?: { id: number }) {
    // Not reporting buttonClick event when pressed Back to dismiss
    if (args?.id) {
      this._onButtonClick?.(args.id);
    }
    AppData?.notificationService.hide();
  }

  private updateVisibility(visible: boolean) {
    this._Content.visible = visible;
    this.getActiveNotification().visible = visible;
    this._HoverBlocker.visible = visible;
  }

  private clearCallbacks() {
    this._onViewed = null;
    this._onButtonClick = null;
  }

  private getActiveNotification(): Notification | FullscreenNotification {
    return this.isFullscreen()
      ? this._FullscreenNotification
      : this._Notification;
  }

  private isFullscreen(): boolean {
    return this._displayType === 'fullscreen';
  }
}
